// ==========================================
// Buddy — mock data
// ==========================================
// Seeded to match the design doc's mockups exactly: Lv 7 Scout, 580/1000 XP,
// 12-day streak at 1.4x, daily quest 2/3, 9 of 24 badges, 3 pinned workflows
// with "+9" behind them.
//
// Everything here is replaced wholesale once the backend lands — see the
// API-SWAP markers in buddy-chat / buddy-workflow / buddy-progress services.
// ==========================================

import { Badge, BuddyMessage, BuddyProgress, BuddyThread, StreakDay, Workflow } from '../models/buddy.types';
import { HABITAT_PROPS, LEVEL_THRESHOLDS } from '../models/buddy.constants';

/** Companion name — the design doc's prop defaults to "BidBuddy". */
export const COMPANION_NAME = 'BB';
export const COMPANION_FULL_NAME = 'BidBuddy';

// ------------------------------------------
// Workflows — 3 pinned on the chip rail, 9 behind the "+9" counter
// ------------------------------------------

export const MOCK_WORKFLOWS: readonly Workflow[] = [
  {
    id: 'email',
    name: 'Email',
    glyph: '✉',
    accent: 'action',
    description: 'Draft and send a reply from the current thread.',
    stepLabels: ['Pull thread context', 'Draft reply', 'Send via Gmail'],
    pinned: true,
    failsAtStep: null,
  },
  {
    id: 'event',
    name: 'Event',
    glyph: '▤',
    accent: 'streak',
    description: 'Book a calendar slot and block travel time.',
    stepLabels: ['Find a mutual slot', 'Send invite', 'Block travel time'],
    pinned: true,
    failsAtStep: null,
  },
  {
    id: 'home',
    name: 'Home',
    glyph: '◈',
    accent: 'xp',
    description: 'Bring the day-start digest into the panel.',
    stepLabels: ['Collect overnight activity', 'Rank by urgency', 'Summarise'],
    pinned: true,
    failsAtStep: null,
  },
  {
    id: 'script',
    name: 'Script',
    glyph: '⌘',
    accent: 'badge',
    description: 'Run a saved shell script against the selected hosts.',
    stepLabels: ['Resolve host list', 'Run script', 'Collect output'],
    pinned: false,
    failsAtStep: null,
  },
  {
    id: 'audit-cron',
    name: 'audit-cron',
    glyph: '◷',
    accent: 'action',
    description: 'Diff crontab against systemd units across every host.',
    stepLabels: ['Read crontab on 3 hosts', 'Diff against systemd units', 'Draft fix + post to #infra'],
    pinned: false,
    failsAtStep: null,
  },
  {
    id: 'bid-chase',
    name: 'bid-chase',
    glyph: '▲',
    accent: 'xp',
    description: 'Nudge every open bid that has gone quiet.',
    stepLabels: ['Find bids idle 5+ days', 'Draft follow-ups', 'Queue for approval'],
    pinned: false,
    failsAtStep: null,
  },
  {
    id: 'site-log',
    name: 'site-log',
    glyph: '◉',
    accent: 'streak',
    description: "Turn yesterday's site photos into a dated visit log.",
    stepLabels: ['Gather photos', 'Match to jobsite', 'Write log entry'],
    pinned: false,
    failsAtStep: null,
  },
  {
    id: 'invoice-run',
    name: 'invoice-run',
    glyph: '§',
    accent: 'action',
    description: 'Assemble invoices for every completed work order.',
    stepLabels: ['Collect completed WOs', 'Build line items', 'Stage for review'],
    pinned: false,
    // Exercises the failure path: BB flips to `oops` and offers retry.
    failsAtStep: 1,
  },
  {
    id: 'standup',
    name: 'standup',
    glyph: '◍',
    accent: 'badge',
    description: 'Compile the standup digest from yesterday.',
    stepLabels: ['Read commits + tickets', 'Group by owner', 'Post to #standup'],
    pinned: false,
    failsAtStep: null,
  },
  {
    id: 'doc-file',
    name: 'doc-file',
    glyph: '▣',
    accent: 'action',
    description: 'File loose documents into the right job folder.',
    stepLabels: ['Scan inbox', 'Classify by job', 'Move + tag'],
    pinned: false,
    failsAtStep: null,
  },
  {
    id: 'expense-sync',
    name: 'expense-sync',
    glyph: '⊞',
    accent: 'streak',
    description: 'Match card charges to receipts and flag the gaps.',
    stepLabels: ['Pull card feed', 'Match receipts', 'Flag unmatched'],
    pinned: false,
    failsAtStep: null,
  },
  {
    id: 'backup-check',
    name: 'backup-check',
    glyph: '◇',
    accent: 'badge',
    description: 'Verify last night’s backups actually restored.',
    stepLabels: ['List snapshots', 'Test restore', 'Report'],
    pinned: false,
    failsAtStep: null,
  },
];

// ------------------------------------------
// Progress — seeded to the mockups
// ------------------------------------------

/** 3b's streak strip: M/T/W hit, Thursday burned the monthly freeze, F hit, S today, S untouched. */
const MOCK_WEEK: StreakDay[] = [
  { label: 'M', state: 'hit' },
  { label: 'T', state: 'hit' },
  { label: 'W', state: 'hit' },
  { label: 'T', state: 'frozen' },
  { label: 'F', state: 'hit' },
  { label: 'S', state: 'today' },
  { label: 'S', state: 'empty' },
];

/** 24 badges, 9 unlocked — the grid in 3b shows the first six. */
const MOCK_BADGES: Badge[] = [
  { id: 'first-light', glyph: '★', label: 'First light', accent: 'badge', unlocked: true, condition: 'Open Buddy on 5 separate days' },
  { id: 'closer', glyph: '✓', label: 'Closer', accent: 'xp', unlocked: true, condition: 'Complete 25 workflows' },
  { id: 'climber', glyph: '▲', label: 'Climber', accent: 'streak', unlocked: true, condition: 'Hold a 7-day streak' },
  { id: 'listener', glyph: '◍', label: 'Listener', accent: 'action', unlocked: true, condition: 'Use voice input 20 times' },
  { id: 'architect', glyph: '◈', label: 'Architect', accent: 'action', unlocked: false, condition: 'Define 5 workflows of your own' },
  { id: 'operator', glyph: '⌘', label: 'Operator', accent: 'badge', unlocked: false, condition: 'Run a workflow on 10 hosts at once' },
  { id: 'early-bird', glyph: '◷', label: 'Early bird', accent: 'streak', unlocked: true, condition: 'Open Buddy before 7am, 5 times' },
  { id: 'scribe', glyph: '▤', label: 'Scribe', accent: 'action', unlocked: true, condition: 'Log 30 site visits' },
  { id: 'courier', glyph: '✉', label: 'Courier', accent: 'action', unlocked: true, condition: 'Send 50 drafted replies' },
  { id: 'auditor', glyph: '§', label: 'Auditor', accent: 'xp', unlocked: true, condition: 'Run 10 audit workflows' },
  { id: 'streak-keeper', glyph: '❄', label: 'Streak keeper', accent: 'badge', unlocked: true, condition: 'Save a streak with a freeze' },
  { id: 'marathon', glyph: '⊞', label: 'Marathon', accent: 'streak', unlocked: false, condition: 'Hold a 30-day streak' },
  { id: 'polyglot', glyph: '◇', label: 'Polyglot', accent: 'badge', unlocked: false, condition: 'Use 8 different workflow types' },
  { id: 'night-owl', glyph: '☾', label: 'Night owl', accent: 'badge', unlocked: false, condition: 'Close a workflow after 10pm' },
  { id: 'rescuer', glyph: '⌁', label: 'Rescuer', accent: 'error', unlocked: false, condition: 'Recover 5 failed workflows' },
  { id: 'curator', glyph: '▣', label: 'Curator', accent: 'action', unlocked: false, condition: 'File 100 documents' },
  { id: 'bookkeeper', glyph: '⊟', label: 'Bookkeeper', accent: 'xp', unlocked: false, condition: 'Reconcile a full month of expenses' },
  { id: 'scout-badge', glyph: '◉', label: 'Scout', accent: 'streak', unlocked: false, condition: 'Reach level 10' },
  { id: 'pilot-badge', glyph: '✦', label: 'Pilot', accent: 'badge', unlocked: false, condition: 'Reach level 15' },
  { id: 'ace-badge', glyph: '✧', label: 'Ace', accent: 'streak', unlocked: false, condition: 'Reach level 20' },
  { id: 'mentor', glyph: '⊕', label: 'Mentor', accent: 'action', unlocked: false, condition: 'Share a workflow with a teammate' },
  { id: 'tinkerer', glyph: '⚙', label: 'Tinkerer', accent: 'badge', unlocked: false, condition: 'Edit a workflow’s parameters 10 times' },
  { id: 'sprinter', glyph: '»', label: 'Sprinter', accent: 'xp', unlocked: false, condition: 'Complete 5 workflows in one hour' },
  { id: 'completionist', glyph: '◆', label: 'Completionist', accent: 'badge', unlocked: false, condition: 'Unlock every other badge' },
];

const SEED_LEVEL = 7;
const SEED_XP_INTO_LEVEL = 580;

/** API-SWAP: replace with GET /api/buddy/progress */
export function buildMockProgress(): BuddyProgress {
  const level = SEED_LEVEL;
  const levelFloor = LEVEL_THRESHOLDS[level];
  const levelCeil = LEVEL_THRESHOLDS[level + 1];

  return {
    level,
    stage: 'scout',
    stageLabel: 'Scout',
    xp: levelFloor + SEED_XP_INTO_LEVEL,
    xpIntoLevel: SEED_XP_INTO_LEVEL,
    xpForLevel: levelCeil - levelFloor,
    streakDays: 12,
    multiplier: 1.4,
    freezeAvailable: false,
    week: MOCK_WEEK.map((d) => ({ ...d })),
    quest: {
      label: 'Close three loops today',
      done: 2,
      total: 3,
      xp: 35,
    },
    challenge: {
      label: "Define 3 workflows you've never run before",
      done: 2,
      total: 3,
      xp: 100,
      daysLeft: 3,
    },
    badges: MOCK_BADGES.map((b) => ({ ...b })),
    habitat: HABITAT_PROPS.map((p) => ({ ...p, unlocked: p.unlockLevel <= level })),
    chatXpToday: 20,
    nudgesToday: 0,
    lastOpenedOn: '',
  };
}

// ------------------------------------------
// Chat — the seeded thread from 3a plus a canned reply pool
// ------------------------------------------

export interface MockSeedMessage {
  role: 'user' | 'assistant';
  text: string;
}

/** The exchange shown in mockup 3a. */
export const MOCK_SEED_THREAD: readonly MockSeedMessage[] = [
  { role: 'user', text: 'how do I stop the nightly sync from double-firing?' },
  {
    role: 'assistant',
    text:
      'Two likely causes — the cron is registered **twice** at boot:\n' +
      '1. A stale entry in `crontab -l`\n' +
      '2. The systemd timer re-arming on failure, so both paths fire',
  },
];

/**
 * Canned assistant replies, streamed token by token to stand in for a model.
 * API-SWAP: replace the whole pool with the streaming response body.
 */
export const MOCK_REPLIES: readonly string[] = [
  'Thu 10:00 with Marcus — booked. Invite sent and travel time blocked.',
  'Two likely causes — the cron is registered **twice** at boot:\n1. A stale entry in `crontab -l`\n2. The systemd timer re-arming on failure',
  'Pulled the last 30 days: **4 bids** have gone quiet past the 5-day mark. Want me to draft follow-ups for all four?',
  'Done. The digest is filed under this week’s jobsite folder and tagged `site-visit`.',
  'That job has **3 open change orders** worth $18,400. The oldest has been sitting 11 days.',
  'I can do that — it needs the `invoice-run` workflow, which touches billing. Run it with defaults, or set parameters first?',
];

/** Suggested replies offered under some assistant turns (3c comfortable density). */
export const MOCK_SUGGESTIONS: readonly string[] = ['Yes, prep it', 'Not now'];

/**
 * Stands in for a live speech-to-text partial. Revealed word by word into the field
 * in grey while BB is listening, then committed on release.
 * API-SWAP: replace with the Web Speech API's interim results.
 */
export const MOCK_TRANSCRIPT = 'book the site walk with Marcus';

// ------------------------------------------
// Glance popover (4b)
// ------------------------------------------

export interface QuickAction {
  workflowId: string;
  label: string;
  glyph: string;
  accent: 'action' | 'streak';
  xp: number;
}

/** The two quick actions on the closed-panel glance. Both run a real workflow. */
export const MOCK_QUICK_ACTIONS: readonly QuickAction[] = [
  { workflowId: 'bid-chase', label: 'Chase 2 open bids', glyph: '✉', accent: 'action', xp: 35 },
  { workflowId: 'site-log', label: "Log yesterday's site visit", glyph: '▤', accent: 'streak', xp: 15 },
];

/** Status line under BB's name in the glance. API-SWAP: derive from the run history. */
export const MOCK_GLANCE_STATUS = {
  headline: 'All quiet',
  detail: 'last run 09:41 · 3 done today',
};

// ------------------------------------------
// Seed threads for the history drawer
// ------------------------------------------
// Dated so the drawer demonstrates every bucket on a fresh install: Today,
// Yesterday, Previous 7 Days, Previous 30 Days, plus one pinned and one archived.
// API-SWAP: replace buildMockThreads() with GET /api/buddy/threads.

const DAY = 86_400_000;

let seedId = 0;
function seedMessage(role: BuddyMessage['role'], text: string, at: number): BuddyMessage {
  seedId += 1;
  return {
    id: `seed-${seedId}`,
    role,
    text,
    createdAt: at,
    streaming: false,
    runId: null,
    reward: null,
    actions: [],
    variant: 'default',
    collapsed: false,
  };
}

interface ThreadSeed {
  title: string;
  ageDays: number;
  pinned?: boolean;
  archived?: boolean;
  turns: [BuddyMessage['role'], string][];
}

const THREAD_SEEDS: readonly ThreadSeed[] = [
  {
    title: 'Nightly sync double-firing',
    ageDays: 0,
    turns: [
      ['user', MOCK_SEED_THREAD[0].text],
      ['assistant', MOCK_SEED_THREAD[1].text],
    ],
  },
  {
    title: 'Q3 bid pack for Marcus',
    ageDays: 0,
    turns: [
      ['user', 'send the Q3 bid pack to Marcus and remind me thursday'],
      ['assistant', 'Sent, and a reminder is set for Thursday 09:00.'],
    ],
  },
  {
    title: 'Site walk scheduling',
    ageDays: 1,
    pinned: true,
    turns: [
      ['user', 'book the site walk with Marcus'],
      ['assistant', 'Thu 10:00, invite sent, travel time blocked.'],
    ],
  },
  {
    title: 'Chasing the quiet bids',
    ageDays: 4,
    turns: [
      ['user', 'which bids have gone quiet?'],
      ['assistant', 'Pulled the last 30 days: **4 bids** have gone quiet past the 5-day mark.'],
    ],
  },
  {
    title: 'Expense reconciliation',
    ageDays: 12,
    turns: [
      ['user', 'match last month card charges to receipts'],
      ['assistant', 'Matched 38 of 41. Three are missing receipts — flagged them for you.'],
    ],
  },
  {
    title: 'Backup restore verification',
    ageDays: 45,
    turns: [
      ['user', 'did last night backups actually restore?'],
      ['assistant', 'All three snapshots restored clean. Report filed under `ops/backups`.'],
    ],
  },
  {
    title: 'Old warehouse label run',
    ageDays: 20,
    archived: true,
    turns: [
      ['user', 'reprint the pallet labels for zone C'],
      ['assistant', 'Queued 48 labels to the warehouse printer.'],
    ],
  },
];

export function buildMockThreads(): BuddyThread[] {
  const now = Date.now();
  return THREAD_SEEDS.map((seed, index) => {
    const at = now - seed.ageDays * DAY - index * 60_000;
    return {
      id: `thread-seed-${index}`,
      title: seed.title,
      titleIsCustom: false,
      messages: seed.turns.map(([role, text]) => seedMessage(role, text, at)),
      runs: {},
      createdAt: at,
      updatedAt: at,
      pinned: seed.pinned ?? false,
      archived: seed.archived ?? false,
    };
  });
}
